import { Fragment } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { DEMAND_PEAK_COLORS } from '~/components/balance/DemandPeak'
import { GcCountdownCard } from '~/components/balance/SummaryBar/GcCountdownCard'
import type { GateCloseFrontier } from '~/models/gateClose'
import { komaTimeRange } from '~/models/komaToTime'
import { formatKwh } from '~/models/kwhDisplay'
import type { WeatherActualKoma } from '~/models/weather/actualKoma'
import { komaWeatherAt } from '~/models/weather/actualKoma'
import {
  TEMPERATURE_EMPTY,
  formatTemperature,
} from '~/models/weather/temperature'
import type { DetailSummary } from '~/pages/BalanceDemandDetail/models/generateMockData'
import { dailyTempRange } from '~/pages/BalanceDemandDetail/models/summaryTemps'

type Props = {
  /** 需要ピーク (backend 算出)。実績が 1 コマも無い、 または実績のあるコマがすべて低圧未着の日は null → 値は「—」。 */
  summary: DetailSummary | null
  frontier: GateCloseFrontier | undefined
  /** GCコマラベル用。未取得時はnull。 */
  gcKoma: number | null
  /** 表示中の日付。当日以外は「次の GC」カードを出さない (spec AC-BDB-022)。 */
  displayDate: Date
  /** 実績気象 48 コマ。ピーク行とヘッダーの気温・天気アイコンの供給元。 */
  weatherActual: readonly WeatherActualKoma[]
}

/** 値が無い時のプレースホルダ (design bg-region: 0 でなくダッシュ)。 */
const EMPTY = TEMPERATURE_EMPTY

/**
 * ヘッダー気温に添える「（14時）」。"HH:MM" の時だけを採る。
 *
 * 実測が 1 コマも無い日 (未来日 / 気象 RPC 失敗) は括弧ごと落として気温の
 * 「—」だけにする。「—（—時）」だと「—時という時刻がある」と読めてしまい、
 * 値が無いことを伝える邪魔になるため。
 */
function formatHourParen(time: string | null, hourSuffix: string): string {
  if (time === null) {
    return ''
  }
  return `(${Number(time.split(':')[0])}${hourSuffix})`
}

const badgeStyle = (bg: string, color: string): React.CSSProperties => ({
  fontSize: fontSizes.sm.value,
  fontWeight: 700,
  padding: '2px 9px',
  borderRadius: '6px',
  letterSpacing: '0.5px',
  background: bg,
  color,
})

/** ピーク値 (xl/700/tabular)。MAX=warning-high / MIN=brand-cyan (design balance.css)。 */
const valueStyle = (color: string): React.CSSProperties => ({
  fontSize: fontSizes.xl.value,
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.1,
  color,
})

const metaStyle: React.CSSProperties = {
  fontSize: fontSizes.sm.value,
  color: LightModeSemanticColors.Text.midEmphasis.value,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
}

/**
 * 需要詳細のサマリーバー (design/balance/bg-region.html summary-bar 準拠)。
 * 上端の行に「次の GC」 の pill、 その下に需要ピークのカード 1 枚 (インバランス予想は
 * design [mock差異] #1829#1 で撤去済み、spec スコープ外)。 2026-10-05 までは
 * 需要ピーク + 次の GC の 2 カードを縦区切りで並べていた (ecl-kpi-grid--2col 相当)。
 */
export const SummaryBar = ({
  summary,
  gcKoma,
  frontier,
  displayDate,
  weatherActual,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // ヘッダーの最高/最低気温は需要ピークとは独立 (= 実績気象だけで決まる)。
  // 需要実績が無い日でも気温が取れていれば表示する。
  const tempRange = dailyTempRange(weatherActual)

  const peakRows = [
    {
      key: 'max',
      label: t('page:balanceDemandDetail.summary.max'),
      ...DEMAND_PEAK_COLORS.max,
      value: summary?.maxActual ?? null,
      time: summary?.maxTime ?? null,
      // summary 不在は koma 0 として引く。komaWeatherAt は範囲外を「気象なし」に
      // 落とす契約なので、ここで同じ fallback を書き足さない。
      weather: komaWeatherAt(weatherActual, summary?.maxKoma ?? 0),
    },
    {
      key: 'min',
      label: t('page:balanceDemandDetail.summary.min'),
      ...DEMAND_PEAK_COLORS.min,
      value: summary?.minActual ?? null,
      time: summary?.minTime ?? null,
      weather: komaWeatherAt(weatherActual, summary?.minKoma ?? 0),
    },
  ]

  return (
    <>
      {/* 次の GC (当日表示時のみ。 spec AC-BDB-021 / AC-BDB-022。 残り 10 分以内は強調 = AC-BDB-027)。
          2026-10-05 の GC カウントダウン統合で KPI カードをやめ、 サマリー上端の行の右端に
          全画面共通の pill を置く (design `balance/bg-region.html` `.summary-gc-row`)。
          `gcKoma` が未取得 (null) の時は出さない — 取得に失敗した画面で
          「次の GC コマ 1 (00:00〜00:30)」 を**確定値のように**見せないため。 */}
      {gcKoma !== null && (
        <GcCountdownCard
          displayDate={displayDate}
          frontier={frontier}
          gcKoma={gcKoma}
        />
      )}
      <Flex
        flexShrink={0}
        overflow="hidden"
        marginBottom="8px"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="lg"
        backgroundColor="Surface.primary"
      >
        {/* 需要ピーク */}
        <Box
          flex={1}
          padding="12px"
          aria-label={t('page:balanceDemandDetail.summary.demandPeak')}
          role="group"
        >
          <Flex
            alignItems="baseline"
            justifyContent="space-between"
            gap="12px"
            marginBottom="4px"
          >
            {/* カード見出し (md/700/mid) は役割 textStyle と一致しないため raw 維持
              (design .summary-bar .ecl-kpi-label)。 */}
            <span
              style={{
                fontSize: fontSizes.md.value,
                fontWeight: 700,
                color: LightModeSemanticColors.Text.midEmphasis.value,
              }}
            >
              {t('page:balanceDemandDetail.summary.demandPeak')}
            </span>
            <Text
              as="span"
              color="Text.midEmphasis"
              fontSize="sm"
              whiteSpace="nowrap"
            >
              {t('page:balanceDemandDetail.summary.tempLabel')}{' '}
              <Text
                as="span"
                color="Text.highEmphasis"
                fontWeight="bold"
                fontVariantNumeric="tabular-nums"
              >
                {formatTemperature(tempRange.maxTemp)}
              </Text>
              {formatHourParen(
                tempRange.maxTime,
                t('page:balanceDemandDetail.summary.hourSuffix'),
              )}
              {'/ '}
              <Text
                as="span"
                color="Text.highEmphasis"
                fontWeight="bold"
                fontVariantNumeric="tabular-nums"
              >
                {formatTemperature(tempRange.minTemp)}
              </Text>
              {formatHourParen(
                tempRange.minTime,
                t('page:balanceDemandDetail.summary.hourSuffix'),
              )}
            </Text>
          </Flex>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto auto 1fr',
              columnGap: '8px',
              rowGap: '2px',
              alignItems: 'baseline',
            }}
          >
            {peakRows.map((row) => (
              <Fragment key={row.key}>
                <span style={badgeStyle(row.badgeBg, row.badgeColor)}>
                  {row.label}
                </span>
                <span style={valueStyle(row.valueColor)}>
                  {row.value === null ? EMPTY : formatKwh(row.value)}
                  <span
                    style={{
                      fontSize: fontSizes.sm.value,
                      fontWeight: 500,
                      marginLeft: '3px',
                      color: LightModeSemanticColors.Text.midEmphasis.value,
                    }}
                  >
                    {t('page:balanceDemandDetail.summary.kwh')}
                  </span>
                </span>
                <span style={metaStyle}>
                  {row.time === null ? EMPTY : komaTimeRange(row.time)}{' '}
                  {/* design `.peak-wicon` (css/balance.css:352) をそのまま写す。
                    13px は design スケール外 (xs=10/sm=12/md=14) の既存値なので
                    fontSize prop 化できない。 `margin: 0 1px` は絵文字の左右に
                    最小の隙間を作るためのもので、 これが無いと時刻と気温の
                    どちらにも詰まって見える。 同じ design クラスを写している
                    `components/balance/DemandPeak` は最初から持っていた。 */}
                  <span
                    style={{ fontSize: '13px', lineHeight: 1, margin: '0 1px' }}
                  >
                    {row.weather.emoji}
                  </span>
                  {formatTemperature(row.weather.temperature)}
                </span>
              </Fragment>
            ))}
          </div>
        </Box>
      </Flex>
    </>
  )
}
